/**
 * Free / consumer email domains we suppress on lead-capture forms so RevOps
 * gets work addresses they can match to a company. Sourced from the canonical
 * free-mail list plus regional providers we have seen come through Marketo.
 *
 * The list is intentionally exhaustive instead of relying on a heuristic
 * because false negatives (a personal email getting through) are far more
 * expensive than false positives (a freelancer with their own domain still
 * has to type it out, no real friction).
 */
const FREE_EMAIL_DOMAIN_LIST = [
  // Google
  'gmail.com',
  'googlemail.com',
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.es',
  'yahoo.com.mx',
  'yahoo.com.br',
  'yahoo.com.ar',
  'ymail.com',
  'rocketmail.com',
  // Microsoft
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.es',
  'hotmail.de',
  'hotmail.it',
  'outlook.com',
  'outlook.es',
  'outlook.fr',
  'outlook.de',
  'outlook.com.br',
  'live.com',
  'live.co.uk',
  'msn.com',
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  // Proton
  'protonmail.com',
  'proton.me',
  'pm.me',
  // AOL / Verizon
  'aol.com',
  'aim.com',
  'verizon.net',
  'sbcglobal.net',
  'comcast.net',
  // GMX / United
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'web.de',
  'mail.com',
  // Russia / Eastern Europe
  'mail.ru',
  'yandex.com',
  'yandex.ru',
  // Asia
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  'naver.com',
  // Privacy / niche
  'fastmail.com',
  'fastmail.fm',
  'tutanota.com',
  'tutanota.de',
  'tuta.io',
  'zoho.com',
  // UK ISPs
  'btinternet.com',
  'sky.com',
  'ntlworld.com',
] as const;

export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set<string>(FREE_EMAIL_DOMAIN_LIST);

/**
 * Form IDs that should reject free email providers. Mirror the edu suppression
 * list so the rules stay aligned across lead-capture forms. RevOps owns this
 * list, add or remove form IDs here when a campaign needs a different rule.
 */
export const SUPPRESS_FREE_EMAIL_FORM_IDS = ['6', '27', '31', '677', '890', '891'] as const;

const normalizeEmail = (email: string | null | undefined): string => (email ?? '').trim().toLowerCase();

export const getEmailDomain = (email: string | null | undefined): string => {
  const value = normalizeEmail(email);
  const at = value.lastIndexOf('@');

  return at === -1 ? '' : value.slice(at + 1);
};

export const isFreeEmail = (email: string | null | undefined): boolean => {
  const domain = getEmailDomain(email);

  return domain ? FREE_EMAIL_DOMAINS.has(domain) : false;
};

/**
 * Iterative two-row Levenshtein. ~20 LOC, no dep, fine for short domain
 * strings. Used to catch user typos like "gmial.com" or "yhaoo.com".
 */
const levenshtein = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  if (m === 0) {
    return n;
  }
  if (n === 0) {
    return m;
  }

  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1).fill(0);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }

  return prev[n];
};

/**
 * Tighter threshold for short domains so "live.com" and "lwe.com" do not
 * collapse together. We use 1 for domains <= 8 chars and 2 above that.
 */
const maxAllowedDistance = (domain: string): number => (domain.length <= 8 ? 1 : 2);

/**
 * Returns the closest known free-email domain when the user's domain looks
 * like a typo of a free provider (Levenshtein distance within the threshold),
 * or null when no near-match is found.
 */
export const suggestFreeEmailDomain = (email: string | null | undefined): string | null => {
  const domain = getEmailDomain(email);
  if (!domain || FREE_EMAIL_DOMAINS.has(domain)) {
    return null;
  }

  const threshold = maxAllowedDistance(domain);
  let best: { domain: string; distance: number } | null = null;

  FREE_EMAIL_DOMAINS.forEach(candidate => {
    if (Math.abs(candidate.length - domain.length) > threshold) {
      return;
    }
    const distance = levenshtein(domain, candidate);
    if (distance <= threshold && (!best || distance < best.distance)) {
      best = { domain: candidate, distance };
    }
  });

  return best ? (best as { domain: string; distance: number }).domain : null;
};

/**
 * High-level "is this email a free provider, or close enough to be a typo of
 * one" check. This is what the form validator should call, not isFreeEmail
 * alone, so misspelled gmail/yahoo/hotmail addresses get blocked too.
 */
export const isLikelyFreeEmail = (email: string | null | undefined): boolean =>
  isFreeEmail(email) || suggestFreeEmailDomain(email) !== null;
